import cn from 'classnames';
import { FilterFn, RowData } from '@tanstack/react-table';
import {
  LegacyFeatures,
  LegacyRow,
  LegacyTableOptions,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel
} from '@tanstack/react-table/legacy';
import { RowTypeConfig, StyleConfig, TableStructureConfig } from './hierarchical-table-constants';

// ---------- Filter values ----------

/**
 * Stringify a cell value for filter display.
 * Numbers are fixed to 2 decimals so filter options match how values render;
 * null/undefined stringify to null (excluded from filter options).
 */
export const stringifyFilterValue = (value: unknown): string | null => {
  if (value == null) {
    return null;
  }
  return typeof value === 'number' ? value.toFixed(2) : String(value);
};

/**
 * Canonical form for filter equality: stringified, lowercased, trimmed.
 *
 * Every comparison between stored filter values, cell values, and dropdown
 * options must go through this so a selection can never silently stop
 * matching its own rows (e.g. values with stray whitespace or mixed casing).
 */
export const normalizeFilterValue = (value: unknown): string | null => {
  const stringValue = stringifyFilterValue(value);
  return stringValue === null ? null : stringValue.toLowerCase().trim();
};

// ---------- Filtering ----------

// These filter functions assume `filterFromLeafRows: true` (set by
// getHierarchicalTableDefaults): a group row survives when it matches on its
// own value OR any of its children pass every active filter. Group rows
// therefore return false for columns they have no value for — their children
// decide survival — which prevents "phantom" groups that pass each filter via
// a different child while no single child passes them all. Leaf rows inherit
// group-level values via config.getInheritedValue so they carry their group's
// verdict; the inherited value comes from the row data itself, never from
// TanStack's row model (resolving parents mid-filter re-enters the model
// being computed).

/** Creates a multi-select filter that handles hierarchical row types */
export const createMultiSelectFilter = <TData extends RowData>(
  config: RowTypeConfig<TData>
): FilterFn<LegacyFeatures, TData> => {
  const filterFn: FilterFn<LegacyFeatures, TData> = (row, columnId, filterValue) => {
    if (!filterValue || !Array.isArray(filterValue)) {
      return true;
    }

    const selectedValues = filterValue as string[];

    const matchesValue = (value: unknown): boolean => {
      const normalized = normalizeFilterValue(value);
      return normalized !== null && selectedValues.includes(normalized);
    };

    const rowType = config.getRowType(row.original);

    if (rowType === 'flattenedGroup') {
      return matchesValue(row.getValue(columnId));
    }

    if (rowType === 'group') {
      const groupValue = row.getValue(columnId);

      if (groupValue != null) {
        return matchesValue(groupValue);
      }

      // Leaf-level column: survival is decided by the children
      return false;
    }

    // Leaf row: match own value, or the inherited group value for group-level columns
    const ownValue = row.getValue(columnId);

    if (ownValue != null) {
      return matchesValue(ownValue);
    }

    return matchesValue(config.getInheritedValue?.(row.original, columnId));
  };

  filterFn.autoRemove = (val: unknown) => !val || (Array.isArray(val) && val.length === 0);

  filterFn.resolveFilterValue = (val: unknown) => {
    if (!Array.isArray(val)) {
      return val;
    }
    return val.map(v => normalizeFilterValue(v) ?? '');
  };

  return filterFn;
};

/** Creates a number range filter that handles hierarchical row types */
export const createNumberRangeFilter = <TData extends RowData>(
  config: RowTypeConfig<TData>
): FilterFn<LegacyFeatures, TData> => {
  const filterFn: FilterFn<LegacyFeatures, TData> = (row, columnId, filterValue) => {
    if (!filterValue || !Array.isArray(filterValue)) {
      return true;
    }

    const [min, max] = filterValue as [number, number];

    const matchesRange = (value: unknown): boolean => {
      if (
        value === 'no data' ||
        value == null ||
        typeof value !== 'number' ||
        Number.isNaN(value)
      ) {
        return false;
      }
      return value >= min && value <= max;
    };

    const rowType = config.getRowType(row.original);

    if (rowType === 'flattenedGroup') {
      return matchesRange(row.getValue(columnId));
    }

    if (rowType === 'group') {
      const groupValue = row.getValue(columnId);

      if (groupValue != null && groupValue !== 'no data') {
        return matchesRange(groupValue);
      }

      // Leaf-level column: survival is decided by the children
      return false;
    }

    // Leaf row: match own value, or the inherited group value for group-level columns
    const ownValue = row.getValue(columnId);

    if (ownValue == null) {
      return matchesRange(config.getInheritedValue?.(row.original, columnId));
    }

    return matchesRange(ownValue);
  };

  filterFn.autoRemove = (val: unknown) => !val || !Array.isArray(val) || val.length !== 2;

  return filterFn;
};

// ---------- Sorting ----------

// TanStack sorts each level of the hierarchy independently (subRows are sorted within
// their parent), so these comparators only ever see sibling rows.

/**
 * Numeric sort where "no data" and missing values sort lowest
 */
export const hierarchicalNumericSort = <TData extends RowData>(
  rowA: LegacyRow<TData>,
  rowB: LegacyRow<TData>,
  columnId: string
): number => {
  const getNumericValue = (row: LegacyRow<TData>): number => {
    const value = row.getValue(columnId);
    return typeof value === 'number' ? value : -Infinity;
  };

  return getNumericValue(rowA) - getNumericValue(rowB);
};

/**
 * Date sort where group rows without a value use their most recent child date
 */
export const hierarchicalDateSort = <TData extends RowData>(
  rowA: LegacyRow<TData>,
  rowB: LegacyRow<TData>,
  columnId: string
): number => {
  const getDateValue = (row: LegacyRow<TData>): number => {
    const value = row.getValue(columnId);

    if (!value && row.subRows.length > 0) {
      const childTimes = row.subRows
        .map(subRow => {
          const childValue = subRow.getValue(columnId);
          return childValue ? new Date(childValue as string).getTime() : -Infinity;
        })
        .filter(time => time !== -Infinity);

      return childTimes.length > 0 ? Math.max(...childTimes) : -Infinity;
    }

    return value ? new Date(value as string).getTime() : -Infinity;
  };

  return getDateValue(rowA) - getDateValue(rowB);
};

// ---------- Table defaults ----------

/**
 * Returns useReactTable options for hierarchical tables.
 * Includes all row models, expanded by default, sort descending first.
 *
 * filterFromLeafRows keeps a group visible when it matches a filter itself OR
 * any of its children pass every active filter — the hierarchical filter
 * functions (createMultiSelectFilter/createNumberRangeFilter) rely on this.
 */
export const getHierarchicalTableDefaults = <TData extends RowData>(
  config: TableStructureConfig<TData>
): Pick<
  LegacyTableOptions<TData>,
  | 'getSubRows'
  | 'getRowId'
  | 'filterFromLeafRows'
  | 'getCoreRowModel'
  | 'getExpandedRowModel'
  | 'getFilteredRowModel'
  | 'getFacetedRowModel'
  | 'getFacetedMinMaxValues'
  | 'getFacetedUniqueValues'
  | 'getSortedRowModel'
  | 'initialState'
  | 'defaultColumn'
> => ({
  getSubRows: config.getSubRows,
  getRowId: config.getRowId,
  filterFromLeafRows: true,
  getCoreRowModel: getCoreRowModel(),
  getExpandedRowModel: getExpandedRowModel(),
  getFilteredRowModel: getFilteredRowModel(),
  getFacetedRowModel: getFacetedRowModel(),
  getFacetedMinMaxValues: getFacetedMinMaxValues(),
  getFacetedUniqueValues: getFacetedUniqueValues(),
  getSortedRowModel: getSortedRowModel(),
  initialState: {
    expanded: true
  },
  defaultColumn: {
    sortDescFirst: true
  }
});

// ---------- Row class names ----------

interface RowClassNameOptions {
  /** Class name for group rows (default: "group-row") */
  groupClassName?: string;
  /** Class name for leaf rows (default: "line-item-row") */
  leafClassName?: string;
  /** Class name for edited rows (default: "edited-row") */
  editedClassName?: string;
}

/** Returns CSS class names based on row type (group/leaf) and edit state */
export const getHierarchicalRowClassName = <TData>(
  row: TData,
  config: RowTypeConfig<TData> & StyleConfig<TData>,
  options: RowClassNameOptions = {}
): string => {
  const {
    groupClassName = 'group-row',
    leafClassName = 'line-item-row',
    editedClassName = 'edited-row'
  } = options;

  const rowType = config.getRowType(row);
  const isGroup = rowType === 'group' || rowType === 'flattenedGroup';
  const isEdited = config.isEdited?.(row) || config.hasEditedChildren?.(row);

  return cn(isGroup ? groupClassName : leafClassName, { [editedClassName]: Boolean(isEdited) });
};
